import React, { useMemo, useRef, useState } from 'react';
import { Button, CloseIcon, Dialog, Flex } from '@fluentui/react-northstar';
import i18next from 'i18next';
import { platformService } from '../../services/platform.service';
import { getCachedFeature } from '../../cache/FeatureCache';
import { getStatusIdByGuid } from '../../../tikit/ticketHelper';
import { LICENSE_FEATURE, LIFECYCLE_STATUS_GUID, STATUS_GUID, TicketApprovalState } from '../../utils/constants';
import { dialogContent, dialogHeader, listOfOpenTask } from '../TicketTasks/TaskCommon/TaskCommon';
import { ResolutionNoteDialog } from '../TicketResolutionNoteComponents/ResolutionNoteDialog';
import { AddOrUpdateResolutionToDraftKB, IResolution } from '../TicketResolutionNoteComponents/ResolutionHelper';
import { appState } from '../../../AppState';
import { useTranslation } from 'react-i18next';
import { InlineComboCell } from './InlineComboCell';
import { reloadAfterSave, reportFailure } from './InlineFieldCells';
import { SaveOutcome } from './inlineCell';
import { TicketProps } from '../../interfaces/ticketproperties.interface';
import { buildCategoryOptions, optionName } from './inlineCategoryOptions';

interface Props {
  /** The row's ticket - the resolution dialog reads it, and its lifecycle decides what is offered. */
  ticket: {
    Id?: number;
    Title?: string;
    StatusId?: number | null;
    TicketLifecycle?: { Status?: { Guid?: string } };
    Resolution?: { AnswerId?: number | null };
  };
  statuses: TicketProps[];
  taskStatuses: TaskStatus[];
  lifecycleEnabled: boolean;
  /** Resolution notes are licensed and switched on: choosing Resolved asks for them. */
  resolutionNotesOn: boolean;
  resolutionNoteConfig: TikitConfiguration;
  /** The status pill the column draws today. */
  renderPill: (statusId: number | null) => React.ReactNode;
  onSaved: () => void | Promise<void>;
  /** Runs before anything is asked or saved, and throws InlineEditRefused to stop it. */
  guard?: () => Promise<void>;
}

interface Warning { taskList: TicketTask[]; approvalsList: TicketApprovals[]; statusId: number; }

/**
 * Status in the ticket list, typable in place like Category, drawn as its pill at rest. Follows the ticket page's
 * rules: never Deflected; not Resolved or Closed while a lifecycle runs; open tasks or pending approvals ask
 * first; Resolved asks for the resolution note when notes are on. Saved as the ticket page saves (EditTicketForm): the
 * Draft KB article first, then the status and the resolution through the same ticket update every other cell uses,
 * and only then is the page fetched again, so the note's columns show it.
 */
export const InlineStatusCell = ({ ticket, statuses, taskStatuses, lifecycleEnabled, resolutionNotesOn, resolutionNoteConfig, renderPill, onSaved, guard }: Props) => {
  const currentState = appState();
  const { t } = useTranslation();
  const resolvedId = getStatusIdByGuid(statuses, STATUS_GUID.RESOLVED);
  const closedId = getStatusIdByGuid(statuses, STATUS_GUID.CLOSED);
  const deflectedId = getStatusIdByGuid(statuses, STATUS_GUID.DEFLECTED);
  const lifecycleRunning = lifecycleEnabled && [LIFECYCLE_STATUS_GUID.NOT_STARTED, LIFECYCLE_STATUS_GUID.IN_PROGRESS]
    .includes(ticket.TicketLifecycle?.Status?.Guid ?? '');

  const options = useMemo(() => buildCategoryOptions(statuses.filter(status =>
    status.Id !== deflectedId && !(lifecycleRunning && (status.Id === resolvedId || status.Id === closedId))
  )), [statuses, lifecycleRunning]);

  const [warning, setWarning] = useState<Warning | null>(null);
  const [askResolution, setAskResolution] = useState(false);
  const [resolution, setResolution] = useState<ResolutionDetail>();
  /** Settles the question a dialog is asking: true to go on, false to put the old status back. */
  const answerRef = useRef<(go: boolean) => void>(() => undefined);
  const ask = (open: () => void) => new Promise<boolean>(settle => { answerRef.current = settle; open(); });

  const openWork = async (): Promise<[TicketTask[], TicketApprovals[]]> => {
    const api = new platformService();
    const tasksOn = await getCachedFeature(LICENSE_FEATURE.TicketTasks);
    const [tasks, approvals] = await Promise.all([
      tasksOn ? api.getTicketTasks(`?$filter=TicketId%20eq%20${ticket.Id}%20and%20IsDeleted%20eq%20false`) : Promise.resolve([]),
      api.getTicketApprovals(ticket.Id)
    ]);
    const withStatus = (tasks ?? []).map((task: TicketTask) => ({ ...task, Status: taskStatuses.find(s => s.Id === task.StatusId) }));
    return [
      listOfOpenTask(taskStatuses, withStatus),
      (approvals ?? []).filter((a: TicketApprovals) => a.Approval?.ApprovalState === TicketApprovalState.Pending)
    ];
  };

  /**
   * The note as the ticket page sends it. The ticket's saved resolution is read from the server - My Work's rows do
   * not carry it - so a ticket resolved before keeps its resolution (its Id) and its Draft KB article (AnswerId):
   * without them the save would make a second resolution, and the helper a second article. With Draft KB the
   * article is written first, as the ticket page does, so its id goes in with the save.
   */
  const prepareNote = async (api: platformService, note: ResolutionDetail): Promise<ResolutionDetail> => {
    const { data } = await api.getTicket(ticket.Id, '?$select=Id,Title&$expand=Resolution');
    const saved: ResolutionDetail | undefined = data?.Resolution ?? undefined;
    let prepared: ResolutionDetail = { ...note, Id: note.Id ?? saved?.Id, AnswerId: note.AnswerId ?? saved?.AnswerId ?? null };
    if (prepared.DraftKB) {
      const answerId = await AddOrUpdateResolutionToDraftKB(prepared, { ...ticket, Title: data?.Title ?? ticket.Title, Resolution: saved }, currentState, t);
      prepared = { ...prepared, AnswerId: answerId };
    }
    return prepared;
  };

  const commit = async (statusId: number): Promise<SaveOutcome> => {
    // Set once the resolution is saved: a failure after it (the status) still reloads the row, so the screen shows
    // what the server now holds.
    let wrote = false;
    try {
      await guard?.();
      const finishing = statusId === resolvedId || statusId === closedId;
      if (finishing) {
        const [taskList, approvalsList] = await openWork();
        if ((taskList.length || approvalsList.length) &&
          !(await ask(() => setWarning({ taskList, approvalsList, statusId })))) return 'cancelled';
      }

      let note: ResolutionDetail | undefined;
      if (statusId === resolvedId && resolutionNotesOn) {
        setResolution(undefined);
        if (!(await ask(() => setAskResolution(true)))) return 'cancelled';
        note = resolutionRef.current;
      }

      const api = new platformService();
      if (note) {
        const prepared = await prepareNote(api, note);
        // An existing resolution is patched on its own, as updateTicket does - awaited here, where updateTicket
        // leaves it running - and a new one goes in with the ticket.
        if (prepared.Id != null) {
          await api.updateTicketResolutionNote(prepared.Id, { ...prepared });
          wrote = true;
          await api.updateTicket(ticket.Id, { StatusId: statusId });
        } else {
          await api.updateTicket(ticket.Id, { StatusId: statusId, Resolution: prepared });
        }
      } else {
        await api.updateTicket(ticket.Id, { StatusId: statusId });
      }
      reloadAfterSave(onSaved);
      return 'saved';
    } catch (error: unknown) {
      // A timeout before anything was written can be checked; one after the resolution was saved is part done.
      const outcome = reportFailure(error, wrote ? undefined : { reload: onSaved });
      if (!wrote) return outcome;
      await Promise.resolve(onSaved()).catch(() => undefined);
      return 'partial';
    }
  };

  // The dialog hands the note over through state; the save reads it after the dialog has settled.
  const resolutionRef = useRef<ResolutionDetail>();
  resolutionRef.current = resolution;

  const settle = (go: boolean) => {
    setWarning(null);
    setAskResolution(false);
    answerRef.current(go);
  };

  const statusName = (id: number | null) => optionName(buildCategoryOptions(statuses), id);

  return (
    <>
      <InlineComboCell
        ticketId={ticket.Id}
        valueId={ticket.StatusId ?? null}
        options={options}
        label={i18next.t('ticket.ticket-list.status')}
        restingTitle={statusName}
        renderResting={renderPill}
        commit={commit}
        held={!!warning || askResolution}
      />
      {warning && (
        <Dialog
          open
          header={dialogHeader(statuses.find(s => s.Id === warning.statusId) as unknown as StatusDetail, ticket.Id, i18next.t)}
          content={dialogContent(warning.taskList, warning.approvalsList)}
          headerAction={{ icon: <CloseIcon />, title: i18next.t('common.buttons.cancel'), onClick: () => settle(false) }}
          footer={
            <Flex gap="gap.small">
              <Flex.Item push>
                <Button content={i18next.t('common.buttons.cancel')} onClick={() => settle(false)} />
              </Flex.Item>
              <Flex.Item>
                <Button content={i18next.t('common.buttons.confirm')} primary onClick={() => settle(true)} />
              </Flex.Item>
            </Flex>
          }
        />
      )}
      {askResolution && (
        <ResolutionNoteDialog
          resolutionProp={{ ticketData: ticket, resolutionNoteConfig } as IResolution}
          // The dialog hides the row it is about, so it names the ticket.
          headerSuffix={`#${ticket.Id} ${ticket.Title ?? ''}`.trim()}
          showDialog={askResolution}
          // Its Save closes the dialog first and saves second, so a close only means Cancel if no save follows.
          setShowDialog={show => { if (!show) setTimeout(() => settle(false), 0); }}
          setUpdatedResolution={setResolution}
          confirmSave={async () => settle(true)}
        />
      )}
    </>
  );
};
